import { permiteRetryImpressao } from '../Utils/PrintOperation';

export interface QueueJob {
    id: string;
    tentativas?: number;
}

type QueueRef<T> = {
    current: T[];
};

interface ProcessQueueOptions<T extends QueueJob> {
    fila: QueueRef<T>;
    executar: (job: T) => Promise<void>;
    aoIniciarTentativa?: (job: T, tentativa: number, restantes: number) => void;
    aoConcluir?: (job: T) => void;
    aoFalharTentativa?: (
        job: T,
        error: unknown,
        tentativa: number,
        maxTentativas: number,
        vaiTentarNovamente: boolean,
    ) => void;
    aoDescartar?: (job: T, error: unknown) => void;
    aoFinalizarJob?: (job: T) => void;
    aoAtualizarFila?: (fila: T[]) => void;
    maxTentativas?: number;
    calcularEsperaRetry?: (tentativa: number) => number;
    intervaloEntreJobs?: number;
    esperar?: (ms: number) => Promise<void>;
}

const esperarPadrao = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Esvazia uma fila de impressão com um único worker e retries sequenciais.
 *
 * O bloqueio contra workers concorrentes fica no chamador. Esta função nunca
 * chama a si mesma e remove cada trabalho pelo id, evitando que um retry remova
 * acidentalmente o próximo item da fila.
 */
export async function processarFilaSequencial<T extends QueueJob>({
    fila,
    executar,
    aoIniciarTentativa,
    aoConcluir,
    aoFalharTentativa,
    aoDescartar,
    aoFinalizarJob,
    aoAtualizarFila,
    maxTentativas = 3,
    calcularEsperaRetry = tentativa => 4000 * tentativa,
    intervaloEntreJobs = 1000,
    esperar = esperarPadrao,
}: ProcessQueueOptions<T>): Promise<void> {
    while (fila.current.length > 0) {
        const job = fila.current[0];
        let concluido = false;
        let ultimoErro: unknown = new Error('Falha de impressão');

        while ((job.tentativas || 0) < maxTentativas) {
            job.tentativas = (job.tentativas || 0) + 1;
            const tentativaAtual = job.tentativas;

            aoIniciarTentativa?.(job, tentativaAtual, Math.max(0, fila.current.length - 1));

            try {
                await executar(job);
                concluido = true;
                aoConcluir?.(job);
                break;
            } catch (error) {
                ultimoErro = error;
                const vaiTentarNovamente = tentativaAtual < maxTentativas && permiteRetryImpressao(error);
                aoFalharTentativa?.(job, error, tentativaAtual, maxTentativas, vaiTentarNovamente);

                if (vaiTentarNovamente) {
                    await esperar(calcularEsperaRetry(tentativaAtual));
                } else {
                    break;
                }
            }
        }

        // A fila pode ter sido limpa enquanto a impressão estava em andamento.
        // Remover pelo id garante que um novo trabalho nunca seja descartado.
        const indiceDoJob = fila.current.findIndex(item => item.id === job.id);
        if (indiceDoJob >= 0) {
            fila.current.splice(indiceDoJob, 1);
        }

        aoAtualizarFila?.([...fila.current]);

        if (!concluido) {
            aoDescartar?.(job, ultimoErro);
        }

        aoFinalizarJob?.(job);

        if (fila.current.length > 0 && intervaloEntreJobs > 0) {
            await esperar(intervaloEntreJobs);
        }
    }
}
